
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { createGroupUrl } from '../../../hooks/useNavigation';

interface MyGroup {
  id: string;
  sequential_id: number;
  name: string;
  description: string;
  cover_image: string;
  category: string;
  privacy: string;
  member_count: number;
}

interface MyGroupsCarouselProps {
  userMemberships: Set<string>;
  onMembershipChange: () => void;
}

const MyGroupsCarousel = ({
  userMemberships,
  onMembershipChange,
}: MyGroupsCarouselProps) => {
  const { user } = useAuth();
  const [myGroups, setMyGroups] = useState<MyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (user) {
      loadMyGroups();
    }
  }, [user, userMemberships]);

  const loadMyGroups = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data: memberships, error: memError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (memError || !memberships || memberships.length === 0) {
        setMyGroups([]);
        setLoading(false);
        return;
      }

      const groupIds = memberships.map((m) => m.group_id);
      const { data: groups, error: grpError } = await supabase
        .from('groups')
        .select(
          'id, sequential_id, name, description, cover_image, category, privacy, member_count',
        )
        .in('id', groupIds)
        .order('name', { ascending: true });

      if (grpError) {
        console.error('Error loading my groups:', grpError);
        setMyGroups([]);
      } else {
        setMyGroups(groups || []);
      }
    } catch (err) {
      console.error('Error:', err);
      setMyGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const checkScrollability = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    checkScrollability();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScrollability);
      window.removeEventListener('resize', checkScrollability);
    };
  }, [myGroups]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 320;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleLeaveGroup = async (
    e: React.MouseEvent,
    groupId: string,
    currentCount: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);

      if (error) throw error;

      await supabase
        .from('groups')
        .update({ member_count: Math.max(0, currentCount - 1) })
        .eq('id', groupId);

      setMyGroups((prev) => prev.filter((g) => g.id !== groupId));
      onMembershipChange();
    } catch (err) {
      console.error('Error leaving group:', err);
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-user-heart-line text-teal-500"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">My Groups</h2>
        </div>
        <div className="flex gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 w-72 h-44 bg-gray-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (myGroups.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-user-heart-line text-teal-500"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">My Groups</h2>
          <span className="ml-1 px-2 py-0.5 bg-teal-50 text-teal-600 text-xs font-medium rounded-full">
            {myGroups.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-8 h-8 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
              canScrollLeft
                ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                : 'border-gray-100 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-8 h-8 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
              canScrollRight
                ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                : 'border-gray-100 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {myGroups.map((group) => (
          <MyGroupCard key={group.id} group={group} onLeave={handleLeaveGroup} />
        ))}
      </div>
    </div>
  );
};

interface MyGroupCardProps {
  group: MyGroup;
  onLeave: (e: React.MouseEvent, groupId: string, memberCount: number) => void;
}

const MyGroupCard = ({ group, onLeave }: MyGroupCardProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const groupUrl = createGroupUrl(group.sequential_id, group.name);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Entertainment: 'bg-purple-100 text-purple-700',
      Production: 'bg-blue-100 text-blue-700',
      Genre: 'bg-pink-100 text-pink-700',
      Regional: 'bg-green-100 text-green-700',
      Professional: 'bg-orange-100 text-orange-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Link
      to={groupUrl}
      className="flex-shrink-0 w-72 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden"
    >
      <div className="relative h-28 overflow-hidden">
        <img
          src={group.cover_image}
          alt={group.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-2.5 left-3 right-3 flex items-end justify-between">
          <h3 className="text-white font-semibold text-sm leading-tight line-clamp-1 drop-shadow-sm flex-1 mr-2">
            {group.name}
          </h3>
          {group.privacy === 'private' && (
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lock-line text-white/80 text-xs"></i>
            </div>
          )}
        </div>
      </div>
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getCategoryColor(
                group.category,
              )}`}
            >
              {group.category}
            </span>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <i className="ri-group-line text-[11px]"></i>
              <span>{group.member_count.toLocaleString()}</span>
            </div>
          </div>
          <button
            onClick={(e) => onLeave(e, group.id, group.member_count)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              isHovering
                ? 'bg-red-50 text-red-500 border border-red-200'
                : 'bg-gray-50 text-gray-400 border border-gray-150'
            }`}
          >
            {isHovering ? (
              <span className="flex items-center gap-1">
                <i className="ri-logout-box-line text-[10px]"></i>
                Leave
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <i className="ri-check-line text-[10px]"></i>
                Joined
              </span>
            )}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default MyGroupsCarousel;
